import './tripScenes.css';
import { tone } from '../audio/sfx';
import { S } from '../core/game';
import { m } from '../i18n';
import { h } from './dom';
import { layers } from './layers';

/**
 * Full-screen animated scenes for the train trips: the view from a train window
 * and the panorama from the Taipei 101 observatory. Everything is drawn
 * procedurally on a canvas; captions and buttons are plain DOM.
 */
export type Route = 'toTaipei' | 'toHualien' | 'home';

// ---------- small helpers ----------
type RGB = [number, number, number];
const clamp = (v: number, a: number, b: number) => (v < a ? a : v > b ? b : v);
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const smooth = (e0: number, e1: number, x: number) => {
  const t = clamp((x - e0) / (e1 - e0), 0, 1);
  return t * t * (3 - 2 * t);
};
const hash = (n: number) => {
  const x = Math.sin(n * 127.1 + 311.7) * 43758.5453;
  return x - Math.floor(x);
};
const hex = (c: string): RGB => {
  const n = parseInt(c.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};
const rgb = (c: RGB, a = 1) => (a >= 1 ? `rgb(${c[0] | 0},${c[1] | 0},${c[2] | 0})` : `rgba(${c[0] | 0},${c[1] | 0},${c[2] | 0},${a})`);
const mixc = (a: RGB, b: RGB, t: number): RGB => [lerp(a[0], b[0], t), lerp(a[1], b[1], t), lerp(a[2], b[2], t)];
const mix = (a: string, b: string, t: number, al = 1) => rgb(mixc(hex(a), hex(b), t), al);

function sizeCanvas(cv: HTMLCanvasElement, w: number, hgt: number): number {
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  cv.width = Math.max(1, Math.round(w * dpr));
  cv.height = Math.max(1, Math.round(hgt * dpr));
  return dpr;
}
function offscreen(w: number, hgt: number, dpr: number): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.round(w * dpr));
  c.height = Math.max(1, Math.round(hgt * dpr));
  const x = c.getContext('2d')!;
  x.setTransform(dpr, 0, 0, dpr, 0, 0);
  return [c, x];
}
function rrect(c: CanvasRenderingContext2D, x: number, y: number, w: number, hh: number, r: number): void {
  c.beginPath();
  c.roundRect(x, y, w, hh, r);
}

// =====================================================================
//  Train ride
// =====================================================================
type Biome = 'paddy' | 'town' | 'city' | 'mount' | 'coast' | 'dusk';
interface Env { skyT: string; skyB: string; far1: string; far2: string; ground: string; field: string; haze: string; mtn: number }
const ENV: Record<Biome, Env> = {
  paddy: { skyT: '#4f9fe0', skyB: '#cfeaf3', far1: '#8fb2b8', far2: '#5f927f', ground: '#79b44c', field: '#9fcf5a', haze: '#d9eef2', mtn: 0.55 },
  town: { skyT: '#5aa3de', skyB: '#d6ebf0', far1: '#93aeb5', far2: '#6c958a', ground: '#7fa25e', field: '#94b86a', haze: '#dcecef', mtn: 0.5 },
  city: { skyT: '#6aa6d8', skyB: '#e1e8e8', far1: '#9fb2ba', far2: '#7f979c', ground: '#8f978c', field: '#a3a99c', haze: '#e3e9e9', mtn: 0.45 },
  mount: { skyT: '#3f97e2', skyB: '#c8e9f6', far1: '#7aa9ac', far2: '#3d7d55', ground: '#3f8a3a', field: '#57a046', haze: '#d2edf4', mtn: 1.25 },
  coast: { skyT: '#2f8be0', skyB: '#bfe7f8', far1: '#86b8c4', far2: '#3f7f58', ground: '#5c9c45', field: '#6fae4e', haze: '#d4f0f8', mtn: 1.0 },
  dusk: { skyT: '#35356b', skyB: '#f7a45e', far1: '#8a5f8a', far2: '#4f3d66', ground: '#5e5a35', field: '#8d7a3a', haze: '#f4b47c', mtn: 0.7 },
};
const SEGS: Record<Route, [number, Biome][]> = {
  toTaipei: [[0, 'paddy'], [0.36, 'town'], [0.68, 'city']],
  toHualien: [[0, 'mount'], [0.52, 'coast']],
  home: [[0, 'dusk']],
};
const TUNNELS: [number, number][] = [[0.15, 0.22], [0.3, 0.36], [0.44, 0.51]];

function biomeAt(route: Route, p: number): Biome {
  const s = SEGS[route];
  let b = s[0][1];
  for (const [at, bi] of s) if (p >= at) b = bi;
  return b;
}
function weights(route: Route, p: number): Partial<Record<Biome, number>> {
  const s = SEGS[route];
  const out: Partial<Record<Biome, number>> = {};
  const f = s.map(([at]) => smooth(at - 0.035, at + 0.035, p));
  s.forEach(([, b], i) => {
    out[b] = (out[b] ?? 0) + (i === 0 ? 1 : f[i]) * (i === s.length - 1 ? 1 : 1 - f[i + 1]);
  });
  return out;
}
function envAt(route: Route, p: number): Record<keyof Env, RGB | number> & { mtn: number } {
  const w = weights(route, p);
  const acc: Record<string, RGB | number> = {};
  const keys: (keyof Env)[] = ['skyT', 'skyB', 'far1', 'far2', 'ground', 'field', 'haze', 'mtn'];
  for (const k of keys) acc[k] = k === 'mtn' ? 0 : [0, 0, 0];
  for (const [b, wt] of Object.entries(w) as [Biome, number][]) {
    for (const k of keys) {
      const v = ENV[b][k];
      if (typeof v === 'number') acc[k] = (acc[k] as number) + v * wt;
      else {
        const c = hex(v);
        const a = acc[k] as RGB;
        acc[k] = [a[0] + c[0] * wt, a[1] + c[1] * wt, a[2] + c[2] * wt];
      }
    }
  }
  return acc as Record<keyof Env, RGB | number> & { mtn: number };
}

interface TrainLayout { W: number; H: number; wx: number; wy: number; ww: number; wh: number; ft: number; s: number; tableY: number; tableH: number }
function trainLayout(W: number, H: number): TrainLayout {
  const s = clamp(Math.min(W, H * 0.8) / 390, 0.8, 2);
  const ft = 13 * s;
  let ww: number, wh: number, wy: number;
  if (W / H < 0.8) {
    ww = W - 2 * (22 * s + ft * 0.4);
    wy = Math.max(70 * s, H * 0.1);
    wh = Math.min(H * 0.56, ww * 1.45);
  } else {
    wh = H * 0.55;
    ww = Math.min(W - 140 * s, wh * 1.9);
    wy = H * 0.09;
  }
  const wx = (W - ww) / 2;
  const tableY = wy + wh + ft;
  return { W, H, wx, wy, ww, wh, ft, s, tableY, tableH: 70 * s };
}

/** Wall, luggage rack and window frame (behind the scenery). */
function drawInteriorBack(c: CanvasRenderingContext2D, L: TrainLayout): void {
  const { W, H, wx, wy, ww, wh, ft, s } = L;
  const g = c.createLinearGradient(0, 0, 0, H);
  g.addColorStop(0, '#d9cbb0');
  g.addColorStop(0.5, '#eadcc0');
  g.addColorStop(1, '#bfa988');
  c.fillStyle = g;
  c.fillRect(0, 0, W, H);
  // Panel seams.
  c.strokeStyle = 'rgba(90,60,30,0.14)';
  c.lineWidth = 1;
  for (const x of [wx - ft - 30 * s, wx + ww + ft + 30 * s]) {
    c.beginPath();
    c.moveTo(x, 0);
    c.lineTo(x, L.tableY);
    c.stroke();
  }
  // Luggage rack.
  const ry = Math.max(14 * s, wy - ft - 34 * s);
  c.fillStyle = 'rgba(60,40,20,0.25)';
  c.fillRect(0, ry + 7 * s, W, 6 * s);
  const rg = c.createLinearGradient(0, ry, 0, ry + 7 * s);
  rg.addColorStop(0, '#f4f1ea');
  rg.addColorStop(1, '#8f8a80');
  c.fillStyle = rg;
  c.fillRect(0, ry, W, 6 * s);
  // Window frame: soft outer shadow, bevelled cream frame, dark rubber seal.
  c.save();
  c.shadowColor = 'rgba(60,35,10,0.35)';
  c.shadowBlur = 14 * s;
  c.shadowOffsetY = 3 * s;
  rrect(c, wx - ft, wy - ft, ww + ft * 2, wh + ft * 2, 26 * s);
  const fg = c.createLinearGradient(0, wy - ft, 0, wy + wh + ft);
  fg.addColorStop(0, '#f6efe0');
  fg.addColorStop(1, '#cbbd9f');
  c.fillStyle = fg;
  c.fill();
  c.restore();
  rrect(c, wx - 3 * s, wy - 3 * s, ww + 6 * s, wh + 6 * s, 19 * s);
  c.fillStyle = '#2b2622';
  c.fill();
  // Little window latch.
  c.fillStyle = '#9a9184';
  rrect(c, wx + ww / 2 - 14 * s, wy - ft + 3 * s, 28 * s, 5 * s, 2.5 * s);
  c.fill();
}

/** Curtains, table and things on it (in front of the scenery). */
function drawInteriorFront(c: CanvasRenderingContext2D, L: TrainLayout): void {
  const { W, H, wx, wy, ww, wh, ft, s, tableY, tableH } = L;
  // Curtains, gathered by a tie at ~62% height.
  const cw = 30 * s;
  for (const side of [0, 1]) {
    const x0 = side ? wx + ww + ft + 6 * s - cw : wx - ft - 6 * s;
    const top = wy - ft - 10 * s;
    const bot = wy + wh + ft * 0.4;
    const tie = top + (bot - top) * 0.62;
    const pinch = 8 * s;
    c.save();
    c.beginPath();
    const outer = side ? x0 + cw : x0;
    const inner = side ? x0 : x0 + cw;
    const innerT = side ? inner - 6 * s : inner + 6 * s;
    const innerP = side ? x0 + cw - pinch * 1.6 : x0 + pinch * 1.6;
    c.moveTo(outer, top);
    c.lineTo(innerT, top);
    c.quadraticCurveTo(inner, tie - 40 * s, innerP, tie);
    c.quadraticCurveTo(inner, tie + 30 * s, inner + (side ? -4 : 4) * s, bot);
    c.lineTo(outer, bot);
    c.closePath();
    const cg = c.createLinearGradient(x0, 0, x0 + cw, 0);
    for (let i = 0; i <= 6; i++) cg.addColorStop(i / 6, i % 2 ? '#2f557f' : '#3f6c9c');
    c.fillStyle = cg;
    c.shadowColor = 'rgba(0,0,0,0.3)';
    c.shadowBlur = 8 * s;
    c.fill();
    c.restore();
    c.fillStyle = '#ffd152';
    rrect(c, Math.min(outer, innerP) - 2 * s, tie - 3 * s, Math.abs(innerP - outer) + 4 * s, 6 * s, 3 * s);
    c.fill();
    // Curtain rail.
    c.fillStyle = '#8f8678';
    c.fillRect(x0 - 2 * s, top - 3 * s, cw + 4 * s, 4 * s);
  }
  // Table top (seen from above, coming towards us) and its front edge.
  const tx0 = wx - ft - 4 * s;
  const tx1 = wx + ww + ft + 4 * s;
  const spread = 22 * s;
  c.beginPath();
  c.moveTo(tx0, tableY);
  c.lineTo(tx1, tableY);
  c.lineTo(tx1 + spread, tableY + tableH);
  c.lineTo(tx0 - spread, tableY + tableH);
  c.closePath();
  const tg = c.createLinearGradient(0, tableY, 0, tableY + tableH);
  tg.addColorStop(0, '#6b4a2e');
  tg.addColorStop(1, '#8a6240');
  c.fillStyle = tg;
  c.fill();
  c.strokeStyle = 'rgba(255,220,170,0.1)';
  c.lineWidth = 1;
  for (let i = 1; i < 6; i++) {
    const y = tableY + (tableH * i) / 6;
    c.beginPath();
    c.moveTo(tx0 - (spread * i) / 6, y + Math.sin(i) * 2);
    c.bezierCurveTo(W * 0.35, y - 2, W * 0.6, y + 3, tx1 + (spread * i) / 6, y);
    c.stroke();
  }
  // Shadow of the window frame on the table.
  const sg = c.createLinearGradient(0, tableY, 0, tableY + 14 * s);
  sg.addColorStop(0, 'rgba(30,15,5,0.45)');
  sg.addColorStop(1, 'rgba(30,15,5,0)');
  c.fillStyle = sg;
  c.fillRect(tx0, tableY, tx1 - tx0, 14 * s);
  const ey = tableY + tableH;
  const eg = c.createLinearGradient(0, ey, 0, ey + 12 * s);
  eg.addColorStop(0, '#a87a4f');
  eg.addColorStop(1, '#4a301c');
  c.fillStyle = eg;
  rrect(c, tx0 - spread, ey, tx1 - tx0 + spread * 2, 12 * s, 4 * s);
  c.fill();
  // Below the table: dark seat area (captions live here).
  const bg = c.createLinearGradient(0, ey + 12 * s, 0, H);
  bg.addColorStop(0, '#24170f');
  bg.addColorStop(1, '#120c08');
  c.fillStyle = bg;
  c.fillRect(0, ey + 12 * s, W, H);
  c.fillStyle = 'rgba(0,0,0,0.25)';
  c.fillRect(0, ey + 12 * s, W, 6 * s);
  // Ticket.
  c.save();
  c.translate(wx + ww * 0.2, tableY + tableH * 0.55);
  c.rotate(-0.12);
  c.fillStyle = 'rgba(0,0,0,0.25)';
  rrect(c, -27 * s, -13 * s + 2 * s, 54 * s, 28 * s, 3 * s);
  c.fill();
  c.fillStyle = '#fff3d6';
  rrect(c, -27 * s, -14 * s, 54 * s, 28 * s, 3 * s);
  c.fill();
  c.fillStyle = '#e67e22';
  c.fillRect(-27 * s, -14 * s, 54 * s, 6 * s);
  c.fillStyle = 'rgba(60,40,20,0.55)';
  c.fillRect(-21 * s, -3 * s, 26 * s, 2.5 * s);
  c.fillRect(-21 * s, 3 * s, 34 * s, 2 * s);
  c.fillRect(-21 * s, 8 * s, 18 * s, 2 * s);
  c.fillStyle = '#e0533d';
  c.beginPath();
  c.arc(16 * s, 4 * s, 5 * s, 0, Math.PI * 2);
  c.fill();
  c.restore();
  // Paper cup of tea.
  const cx = wx + ww * 0.78;
  const cy = tableY + tableH * 0.62;
  c.fillStyle = 'rgba(0,0,0,0.28)';
  c.beginPath();
  c.ellipse(cx + 3 * s, cy + 2 * s, 15 * s, 5 * s, 0, 0, Math.PI * 2);
  c.fill();
  c.beginPath();
  c.moveTo(cx - 14 * s, cy - 34 * s);
  c.lineTo(cx + 14 * s, cy - 34 * s);
  c.lineTo(cx + 10 * s, cy);
  c.lineTo(cx - 10 * s, cy);
  c.closePath();
  const pg = c.createLinearGradient(cx - 14 * s, 0, cx + 14 * s, 0);
  pg.addColorStop(0, '#d9d2c2');
  pg.addColorStop(0.4, '#fffaf0');
  pg.addColorStop(1, '#c9c0ad');
  c.fillStyle = pg;
  c.fill();
  c.fillStyle = '#2fae66';
  c.beginPath();
  c.moveTo(cx - 12.6 * s, cy - 22 * s);
  c.lineTo(cx + 12.6 * s, cy - 22 * s);
  c.lineTo(cx + 11.7 * s, cy - 14 * s);
  c.lineTo(cx - 11.7 * s, cy - 14 * s);
  c.closePath();
  c.fill();
  c.fillStyle = '#a4672a';
  c.beginPath();
  c.ellipse(cx, cy - 34 * s, 14 * s, 3.5 * s, 0, 0, Math.PI * 2);
  c.fill();
  c.strokeStyle = '#efe7d6';
  c.lineWidth = 1.5 * s;
  c.stroke();
}

// ---- scenery pieces (all relative to a base line `by`, scaled by `s`) ----
function tree(c: CanvasRenderingContext2D, x: number, by: number, s: number, col: string, k: number): void {
  c.fillStyle = '#5a4030';
  c.fillRect(x - 1.5 * s, by - 10 * s, 3 * s, 10 * s);
  c.fillStyle = col;
  const r = (7 + k * 5) * s;
  c.beginPath();
  c.arc(x, by - 10 * s - r * 0.6, r, 0, Math.PI * 2);
  c.arc(x - r * 0.7, by - 10 * s - r * 0.1, r * 0.7, 0, Math.PI * 2);
  c.arc(x + r * 0.7, by - 10 * s - r * 0.2, r * 0.75, 0, Math.PI * 2);
  c.fill();
}
function farmhouse(c: CanvasRenderingContext2D, x: number, by: number, s: number, dusk: number): void {
  const w = 40 * s;
  const hh = 15 * s;
  c.fillStyle = mix('#f2ead8', '#6e5a5a', dusk);
  c.fillRect(x, by - hh, w, hh);
  c.fillStyle = mix('#c8553a', '#5a2c2c', dusk);
  c.beginPath();
  c.moveTo(x - 4 * s, by - hh);
  c.quadraticCurveTo(x + w / 2, by - hh - 2 * s, x + w + 4 * s, by - hh);
  c.lineTo(x + w - 3 * s, by - hh - 9 * s);
  c.lineTo(x + 3 * s, by - hh - 9 * s);
  c.closePath();
  c.fill();
  c.fillStyle = dusk > 0.5 ? '#ffd27a' : '#6b4a32';
  c.fillRect(x + w / 2 - 3 * s, by - 9 * s, 6 * s, 9 * s);
  c.fillStyle = dusk > 0.5 ? '#ffe39a' : '#5d8fb0';
  c.fillRect(x + 6 * s, by - 10 * s, 6 * s, 5 * s);
  c.fillRect(x + w - 12 * s, by - 10 * s, 6 * s, 5 * s);
}
function temple(c: CanvasRenderingContext2D, x: number, by: number, s: number): void {
  const w = 64 * s;
  const cx = x + w / 2;
  c.fillStyle = '#d9cfbd';
  c.fillRect(x - 4 * s, by - 4 * s, w + 8 * s, 4 * s);
  c.fillStyle = '#b8352a';
  c.fillRect(x + 6 * s, by - 22 * s, w - 12 * s, 18 * s);
  c.fillStyle = '#7a1c16';
  for (let i = 0; i < 5; i++) c.fillRect(x + 9 * s + i * ((w - 22 * s) / 4), by - 22 * s, 3 * s, 18 * s);
  c.fillStyle = '#4a2a18';
  c.fillRect(cx - 6 * s, by - 15 * s, 12 * s, 11 * s);
  const roof = (y: number, half: number, lift: number, col: string) => {
    c.fillStyle = col;
    c.beginPath();
    c.moveTo(cx - half - 6 * s, y - lift);
    c.quadraticCurveTo(cx - half * 0.6, y + 2 * s, cx, y + 1 * s);
    c.quadraticCurveTo(cx + half * 0.6, y + 2 * s, cx + half + 6 * s, y - lift);
    c.lineTo(cx + half * 0.7, y - 10 * s);
    c.lineTo(cx - half * 0.7, y - 10 * s);
    c.closePath();
    c.fill();
    // Swallowtail ridge with upturned ends.
    c.strokeStyle = '#2f9a7a';
    c.lineWidth = 2.2 * s;
    c.beginPath();
    c.moveTo(cx - half * 0.95, y - 16 * s);
    c.quadraticCurveTo(cx - half * 0.7, y - 10 * s, cx, y - 11 * s);
    c.quadraticCurveTo(cx + half * 0.7, y - 10 * s, cx + half * 0.95, y - 16 * s);
    c.stroke();
    c.fillStyle = '#ffd152';
    c.beginPath();
    c.arc(cx, y - 13 * s, 2.4 * s, 0, Math.PI * 2);
    c.fill();
  };
  roof(by - 22 * s, w / 2, 5 * s, '#e0782e');
  roof(by - 36 * s, w / 2 - 12 * s, 6 * s, '#e88a3a');
  c.fillStyle = '#ff4a3a';
  for (const dx of [-18, 18]) {
    c.beginPath();
    c.ellipse(cx + dx * s, by - 19 * s, 2.5 * s, 3.2 * s, 0, 0, Math.PI * 2);
    c.fill();
  }
}
const HOUSE_COLS = ['#e8e1d2', '#d7cfc0', '#c9d6dc', '#e6d3b8', '#d8dcc8', '#f0e8da'];
function shophouses(c: CanvasRenderingContext2D, x: number, by: number, s: number, seed: number): void {
  let cx = x;
  const n = 2 + Math.floor(hash(seed) * 3);
  for (let i = 0; i < n; i++) {
    const w = (14 + hash(seed + i * 3.1) * 8) * s;
    const hh = (26 + Math.floor(hash(seed + i * 7.7) * 3) * 9) * s;
    c.fillStyle = HOUSE_COLS[Math.floor(hash(seed + i) * HOUSE_COLS.length)];
    c.fillRect(cx, by - hh, w, hh);
    c.fillStyle = 'rgba(0,0,0,0.12)';
    c.fillRect(cx + w - 2 * s, by - hh, 2 * s, hh);
    c.fillStyle = '#9a9a92';
    c.fillRect(cx + 2 * s, by - hh - 4 * s, w * 0.5, 4 * s);
    c.fillStyle = '#5b7f99';
    for (let fy = by - hh + 5 * s; fy < by - 12 * s; fy += 9 * s) {
      c.fillRect(cx + 3 * s, fy, w - 6 * s, 3.5 * s);
    }
    c.fillStyle = '#4b3a2e';
    c.fillRect(cx + 1 * s, by - 8 * s, w - 2 * s, 8 * s);
    if (hash(seed + i * 5.3) > 0.45) {
      c.fillStyle = hash(seed + i) > 0.5 ? '#e0533d' : '#f5b62a';
      c.fillRect(cx + w - 6 * s, by - hh + 4 * s, 5 * s, Math.min(hh - 14 * s, 20 * s));
      c.fillStyle = 'rgba(255,255,255,0.75)';
      for (let k = 0; k < 3; k++) c.fillRect(cx + w - 4.5 * s, by - hh + 7 * s + k * 5 * s, 2 * s, 2.5 * s);
    }
    cx += w;
  }
}
function cityTower(c: CanvasRenderingContext2D, x: number, by: number, s: number, seed: number, haze: RGB): void {
  const w = (20 + hash(seed) * 18) * s;
  const hh = (50 + hash(seed + 2) * 80) * s;
  const glass = hash(seed + 4) > 0.5;
  const base = glass ? '#7fa6bf' : '#c9c4b8';
  c.fillStyle = mix(base, '#000000', 0, 1);
  const g = c.createLinearGradient(x, 0, x + w, 0);
  g.addColorStop(0, rgb(mixc(hex(base), haze, 0.25)));
  g.addColorStop(1, rgb(mixc(hex(base), [40, 50, 60], 0.3)));
  c.fillStyle = g;
  c.fillRect(x, by - hh, w, hh);
  c.fillStyle = glass ? 'rgba(230,245,255,0.35)' : 'rgba(70,90,110,0.45)';
  if (glass) for (let fy = by - hh + 4 * s; fy < by; fy += 5 * s) c.fillRect(x, fy, w, 1.2 * s);
  else
    for (let fy = by - hh + 4 * s; fy < by - 3 * s; fy += 6 * s)
      for (let fx = x + 3 * s; fx < x + w - 4 * s; fx += 5 * s) c.fillRect(fx, fy, 3 * s, 3 * s);
  c.fillStyle = '#8a8a86';
  c.fillRect(x + w * 0.3, by - hh - 5 * s, w * 0.35, 5 * s);
}
function hill(c: CanvasRenderingContext2D, x: number, by: number, s: number, seed: number, col: RGB): void {
  const w = (120 + hash(seed) * 90) * s;
  const hh = (40 + hash(seed + 1) * 50) * s;
  c.fillStyle = rgb(col);
  c.beginPath();
  c.moveTo(x - w * 0.2, by + 2);
  for (let i = 0; i <= 24; i++) {
    const u = i / 24;
    const y = by - hh * Math.sin(u * Math.PI) ** 0.8 - Math.sin(u * 37 + seed) * 3 * s;
    c.lineTo(x - w * 0.2 + u * w * 1.4, y);
  }
  c.closePath();
  c.fill();
  c.fillStyle = 'rgba(10,50,20,0.16)';
  for (let i = 0; i < 9; i++) {
    const u = 0.15 + hash(seed + i) * 0.7;
    const px = x - w * 0.2 + u * w * 1.4;
    const py = by - hh * Math.sin(u * Math.PI) ** 0.8 + 8 * s + hash(seed + i * 2) * hh * 0.5;
    c.beginPath();
    c.arc(px, py, (5 + hash(seed - i) * 6) * s, 0, Math.PI * 2);
    c.fill();
  }
}
/** A headland: forested top, a sheer rock face dropping into the sea, surf at its foot. */
function cliff(c: CanvasRenderingContext2D, x: number, hy: number, by: number, s: number, seed: number, t: number): void {
  const w = (170 + hash(seed + 3) * 90) * s;
  const hh = (42 + hash(seed + 4) * 34) * s;
  const base = lerp(hy, by, 0.55);
  const face = 26 * s;
  const ridgeY = (u: number) => base - hh * (0.62 + 0.38 * Math.sin(u * Math.PI * 0.5)) - Math.sin(u * 23 + seed) * 3 * s;
  // Rock face (front, steep).
  c.beginPath();
  c.moveTo(x, base);
  for (let i = 0; i <= 20; i++) {
    const u = i / 20;
    c.lineTo(x + face * 0.6 + u * w, ridgeY(u) + 4 * s);
  }
  c.lineTo(x + w + face, base);
  c.closePath();
  const rg = c.createLinearGradient(0, base - hh, 0, base);
  rg.addColorStop(0, '#9b9584');
  rg.addColorStop(1, '#6d6a5e');
  c.fillStyle = rg;
  c.fill();
  // Vertical streaks in the rock.
  c.strokeStyle = 'rgba(60,55,45,0.35)';
  c.lineWidth = 1.2 * s;
  for (let i = 0; i < 9; i++) {
    const u = hash(seed + i * 2.3);
    const sx = x + face * 0.6 + u * w;
    c.beginPath();
    c.moveTo(sx, ridgeY(u) + 14 * s);
    c.lineTo(sx - 4 * s, base - 4 * s);
    c.stroke();
  }
  // Forest cap, slightly set back.
  c.beginPath();
  c.moveTo(x + face * 0.6, ridgeY(0) + 10 * s);
  for (let i = 0; i <= 20; i++) {
    const u = i / 20;
    c.lineTo(x + face + u * w, ridgeY(u) - 2 * s);
  }
  for (let i = 20; i >= 0; i--) {
    const u = i / 20;
    c.lineTo(x + face * 0.6 + u * w, ridgeY(u) + (16 + Math.sin(u * 31 + seed) * 6) * s);
  }
  c.closePath();
  c.fillStyle = '#3d7d45';
  c.fill();
  c.fillStyle = 'rgba(20,60,25,0.25)';
  for (let i = 0; i < 6; i++) {
    const u = hash(seed + i * 5.7);
    c.beginPath();
    c.arc(x + face + u * w, ridgeY(u) + 6 * s, (4 + hash(seed - i) * 5) * s, 0, Math.PI * 2);
    c.fill();
  }
  // Surf.
  c.fillStyle = 'rgba(255,255,255,0.9)';
  for (let i = 0; i < 4; i++) {
    const k = Math.sin(t * 2.2 + i * 1.3 + seed);
    c.globalAlpha = 0.5 + 0.4 * k;
    c.fillRect(x - 6 * s + i * 3 * s, base - 1 * s + i * 1.4 * s, w + face + 8 * s - i * 4 * s, (1.2 + (i === 0 ? 1 : 0)) * s);
  }
  c.globalAlpha = 1;
}

function palm(c: CanvasRenderingContext2D, x: number, by: number, s: number, col: string): void {
  c.strokeStyle = '#7b6a50';
  c.lineWidth = 1.6 * s;
  c.beginPath();
  c.moveTo(x, by);
  c.lineTo(x + 1 * s, by - 30 * s);
  c.stroke();
  c.strokeStyle = col;
  c.lineWidth = 2 * s;
  for (const a of [-2.6, -2.0, -1.2, -0.5, 0.1]) {
    c.beginPath();
    c.moveTo(x + 1 * s, by - 30 * s);
    c.quadraticCurveTo(x + Math.cos(a) * 8 * s, by - 34 * s + Math.sin(a) * 4 * s, x + Math.cos(a) * 12 * s, by - 28 * s);
    c.stroke();
  }
}
function scooter(c: CanvasRenderingContext2D, x: number, by: number, s: number, seed: number, t: number): void {
  const bob = Math.sin(t * 20 + seed) * 0.4 * s;
  c.fillStyle = '#222';
  c.beginPath();
  c.arc(x - 5 * s, by - 2.2 * s, 2.2 * s, 0, Math.PI * 2);
  c.arc(x + 5 * s, by - 2.2 * s, 2.2 * s, 0, Math.PI * 2);
  c.fill();
  c.fillStyle = ['#e0533d', '#2f7fc1', '#f5f5f0', '#2fae66'][Math.floor(hash(seed) * 4)];
  rrect(c, x - 7 * s, by - 7 * s + bob, 12 * s, 4 * s, 2 * s);
  c.fill();
  c.fillRect(x + 3 * s, by - 10 * s + bob, 2 * s, 5 * s);
  c.fillStyle = '#3a3a4a';
  c.fillRect(x - 3 * s, by - 13 * s + bob, 4 * s, 6 * s);
  c.fillStyle = ['#ffd152', '#e0533d', '#ffffff', '#2f7fc1'][Math.floor(hash(seed + 1) * 4)];
  c.beginPath();
  c.arc(x - 1 * s, by - 15 * s + bob, 2.6 * s, 0, Math.PI * 2);
  c.fill();
}
function egret(c: CanvasRenderingContext2D, x: number, y: number, s: number, fly: number, t: number): void {
  c.fillStyle = '#ffffff';
  c.strokeStyle = '#ffffff';
  if (fly) {
    const f = Math.sin(t * 7 + x * 0.1);
    c.beginPath();
    c.ellipse(x, y, 4 * s, 1.6 * s, 0, 0, Math.PI * 2);
    c.fill();
    c.lineWidth = 1.4 * s;
    c.beginPath();
    c.moveTo(x - 7 * s, y - f * 5 * s);
    c.quadraticCurveTo(x - 3 * s, y - 2 * s, x, y);
    c.quadraticCurveTo(x + 3 * s, y - 2 * s, x + 7 * s, y - f * 5 * s);
    c.stroke();
    c.fillStyle = '#f5b62a';
    c.fillRect(x + 4 * s, y - 0.5 * s, 2 * s, 1 * s);
    return;
  }
  c.beginPath();
  c.ellipse(x, y - 4 * s, 3.4 * s, 2 * s, -0.3, 0, Math.PI * 2);
  c.fill();
  c.lineWidth = 1.1 * s;
  c.beginPath();
  c.moveTo(x + 2 * s, y - 5 * s);
  c.quadraticCurveTo(x + 4 * s, y - 8 * s, x + 2.5 * s, y - 10 * s);
  c.stroke();
  c.fillStyle = '#f5b62a';
  c.fillRect(x + 2.5 * s, y - 10.5 * s, 2.4 * s, 0.9 * s);
  c.strokeStyle = 'rgba(40,40,40,0.6)';
  c.beginPath();
  c.moveTo(x, y - 2 * s);
  c.lineTo(x, y + 1 * s);
  c.stroke();
}
function taipei101(c: CanvasRenderingContext2D, x: number, by: number, hh: number, col: RGB, light: RGB): void {
  const u = hh / 100;
  c.fillStyle = rgb(col);
  c.fillRect(x - 7 * u, by - 10 * u, 14 * u, 10 * u);
  let y = by - 10 * u;
  for (let i = 0; i < 8; i++) {
    const sh = 8 * u;
    c.beginPath();
    c.moveTo(x - 4.6 * u, y);
    c.lineTo(x + 4.6 * u, y);
    c.lineTo(x + 6 * u, y - sh);
    c.lineTo(x - 6 * u, y - sh);
    c.closePath();
    c.fillStyle = rgb(col);
    c.fill();
    c.fillStyle = rgb(light, 0.5);
    c.fillRect(x - 6 * u, y - sh, 12 * u, 0.7 * u);
    y -= sh;
  }
  c.fillStyle = rgb(col);
  c.fillRect(x - 3.4 * u, y - 7 * u, 6.8 * u, 7 * u);
  c.fillRect(x - 2 * u, y - 11 * u, 4 * u, 4 * u);
  c.fillRect(x - 0.6 * u, y - 22 * u, 1.2 * u, 11 * u);
}

function tunnelDark(route: Route, t: number, T: number): number {
  if (route !== 'toHualien') return 0;
  let d = 0;
  for (const [a, b] of TUNNELS) {
    const ta = a * T;
    const tb = b * T;
    d = Math.max(d, smooth(ta - 0.12, ta + 0.12, t) * (1 - smooth(tb - 0.15, tb + 0.15, t)));
  }
  return d;
}

function drawScenery(c: CanvasRenderingContext2D, L: TrainLayout, route: Route, t: number, T: number): void {
  const { ww, wh } = L;
  const s = clamp(wh / 300, 0.8, 2.2);
  const p = clamp(t / T, 0, 1);
  const env = envAt(route, p);
  const w = weights(route, p);
  const wDusk = w.dusk ?? 0;
  const wCoast = w.coast ?? 0;
  const wCity = w.city ?? 0;
  const pace = route === 'home' ? 0.8 : 1;
  const vFar = 14 * s * pace;
  const vFar2 = 34 * s * pace;
  const vMid = 120 * s * pace;
  const vGround = 300 * s * pace;
  const vNear = 900 * s * pace;
  const hy = wh * 0.5 + Math.sin(t * 9) * 0.4 * s;
  const skyT = env.skyT as RGB;
  const skyB = env.skyB as RGB;
  const haze = env.haze as RGB;

  // Sky.
  const sg = c.createLinearGradient(0, 0, 0, hy);
  sg.addColorStop(0, rgb(skyT));
  sg.addColorStop(1, rgb(skyB));
  c.fillStyle = sg;
  c.fillRect(0, 0, ww, hy + 2);
  // Sun.
  if (wDusk > 0.01) {
    const sx = ww * 0.68 - t * vFar * 0.3;
    const sy = hy - 34 * s;
    const glow = c.createRadialGradient(sx, sy, 0, sx, sy, 120 * s);
    glow.addColorStop(0, `rgba(255,214,140,${0.85 * wDusk})`);
    glow.addColorStop(1, 'rgba(255,160,90,0)');
    c.fillStyle = glow;
    c.fillRect(0, 0, ww, hy);
    c.fillStyle = `rgba(255,236,190,${wDusk})`;
    c.beginPath();
    c.arc(sx, sy, 20 * s, 0, Math.PI * 2);
    c.fill();
  }
  if (wDusk < 0.99) {
    const sx = ww * 0.82;
    const sy = hy * 0.22;
    const glow = c.createRadialGradient(sx, sy, 0, sx, sy, 70 * s);
    glow.addColorStop(0, `rgba(255,255,240,${0.9 * (1 - wDusk)})`);
    glow.addColorStop(0.15, `rgba(255,255,230,${0.5 * (1 - wDusk)})`);
    glow.addColorStop(1, 'rgba(255,255,230,0)');
    c.fillStyle = glow;
    c.fillRect(sx - 70 * s, sy - 70 * s, 140 * s, 140 * s);
  }
  // Clouds.
  for (let i = 0; i < 7; i++) {
    const span = ww + 240 * s;
    const cx = ((hash(i) * span - t * (6 + i * 2) * s) % span + span) % span - 120 * s;
    const cy = hy * (0.15 + hash(i + 9) * 0.5);
    const r = (14 + hash(i + 3) * 18) * s;
    c.fillStyle = wDusk > 0.5 ? 'rgba(255,190,170,0.55)' : 'rgba(255,255,255,0.8)';
    c.beginPath();
    c.ellipse(cx, cy, r * 2.2, r * 0.7, 0, 0, Math.PI * 2);
    c.ellipse(cx - r, cy - r * 0.2, r * 1.1, r * 0.75, 0, 0, Math.PI * 2);
    c.ellipse(cx + r * 0.6, cy - r * 0.45, r * 1.2, r * 0.9, 0, 0, Math.PI * 2);
    c.fill();
  }
  // Far mountains (two layers). On the coast they break into headlands over the sea.
  const ridge = (x: number, amp: number, k: number, seed: number) =>
    amp * (0.55 + 0.25 * Math.sin(x * 0.006 * k + seed) + 0.15 * Math.sin(x * 0.017 * k + seed * 2) + 0.05 * Math.sin(x * 0.05 * k));
  const coastMask = (x: number) => lerp(1, smooth(0.15, 0.6, Math.sin(x * 0.0035 + 1.3)) ** 0.7, wCoast);
  const mtn = env.mtn;
  const far1 = env.far1 as RGB;
  const far2 = env.far2 as RGB;
  const layer = (scroll: number, amp: number, k: number, seed: number, col: RGB, masked: boolean) => {
    c.fillStyle = rgb(col);
    c.beginPath();
    c.moveTo(0, hy + 2);
    for (let x = 0; x <= ww + 6; x += 6) {
      const X = x + scroll;
      const m2 = masked ? coastMask(X) : 1;
      c.lineTo(x, hy + 1 - ridge(X, amp, k, seed) * m2);
    }
    c.lineTo(ww, hy + 2);
    c.closePath();
    c.fill();
  };
  layer(t * vFar, 70 * s * mtn, 1, 1, far1, true);
  // City skyline in front of the far range.
  if (wCity > 0.01) {
    c.fillStyle = rgb(mixc(far1, [120, 135, 145], 0.4), wCity);
    const scroll = t * vFar2 * 0.7;
    const cw = 9 * s;
    const i0 = Math.floor(scroll / cw);
    for (let i = i0; i < i0 + ww / cw + 2; i++) {
      const hh = (8 + hash(i * 1.7) ** 2 * 46) * s;
      c.fillRect(i * cw - scroll, hy - hh, cw + 0.5, hh + 2);
    }
    // Taipei 101 rises out of the haze near the end.
    if (route === 'toTaipei') {
      const k = smooth(0.7, 0.98, p);
      const x101 = lerp(ww * 1.12, ww * 0.62, k);
      const col = mixc(hex('#5f8f8f'), skyB, 0.35);
      taipei101(c, x101, hy + 1, Math.min(wh * 0.6, 210 * s), col, hex('#e8fff8'));
    }
  }
  layer(t * vFar2, 46 * s * mtn, 1.7, 4, mixc(far2, haze, 0.15), true);
  // Haze at the horizon.
  const hg = c.createLinearGradient(0, hy - 40 * s, 0, hy + 4);
  hg.addColorStop(0, rgb(haze, 0));
  hg.addColorStop(1, rgb(haze, 0.55));
  c.fillStyle = hg;
  c.fillRect(0, hy - 40 * s, ww, 44 * s);

  // Ground (fields in parallax bands) and, on the coast, the sea.
  const ground = env.ground as RGB;
  const field = env.field as RGB;
  c.fillStyle = rgb(ground);
  c.fillRect(0, hy, ww, wh - hy);
  const bands = 6;
  for (let k = 0; k < bands; k++) {
    const y0 = hy + (wh - hy) * (k / bands) ** 1.5;
    const y1 = hy + (wh - hy) * ((k + 1) / bands) ** 1.5;
    const v = lerp(vMid * 0.6, vGround, (k + 0.5) / bands);
    const sp = (50 + k * 40) * s;
    const scroll = t * v;
    const i0 = Math.floor(scroll / sp);
    for (let i = i0; i < i0 + ww / sp + 2; i++) {
      const x = i * sp - scroll;
      const r = hash(i * 13.1 + k * 91);
      const isWater = (w.paddy ?? 0) + wDusk * 0.6 > 0.4 && r > 0.55;
      if (isWater) c.fillStyle = rgb(mixc(skyB, ground, 0.25 + 0.15 * k / bands));
      else c.fillStyle = rgb(mixc(field, ground, r));
      c.fillRect(x, y0, sp + 0.5, y1 - y0 + 0.5);
      c.fillStyle = 'rgba(40,50,20,0.25)';
      c.fillRect(x, y0, 1.2 * s, y1 - y0);
      if (isWater) {
        c.fillStyle = 'rgba(255,255,255,0.25)';
        c.fillRect(x + sp * 0.2, y0 + (y1 - y0) * 0.4, sp * 0.4, 1);
      }
      // Egrets standing in the paddies.
      if ((w.paddy ?? 0) > 0.3 && k >= 1 && k <= 3 && hash(i * 7.3 + k) > 0.8) egret(c, x + sp * 0.5, y1 - 2 * s, s * (0.6 + k * 0.2), 0, t);
    }
  }
  if (wCoast > 0.01) {
    const seaB = hy + (wh - hy) * 0.72;
    const g = c.createLinearGradient(0, hy, 0, seaB);
    g.addColorStop(0, rgb(hex('#1b5ea8'), wCoast));
    g.addColorStop(1, rgb(hex('#35a8c8'), wCoast));
    c.fillStyle = g;
    c.fillRect(0, hy, ww, seaB - hy);
    // Wave dashes moving at depth-dependent speeds.
    for (let k = 0; k < 7; k++) {
      const y = hy + (seaB - hy) * ((k + 0.5) / 7) ** 1.4;
      const v = lerp(vFar2, vGround * 0.6, k / 7);
      const sp = (24 + k * 18) * s;
      const scroll = t * v;
      const i0 = Math.floor(scroll / sp);
      c.fillStyle = `rgba(255,255,255,${0.55 * wCoast})`;
      for (let i = i0; i < i0 + ww / sp + 2; i++) {
        const r = hash(i * 3.7 + k * 17);
        const a = 0.5 + 0.5 * Math.sin(t * 3 + r * 6);
        if (r < 0.4) continue;
        c.globalAlpha = a;
        c.fillRect(i * sp - scroll + r * sp * 0.5, y + Math.sin(t * 2 + i) * 0.8 * s, (4 + k * 2.2) * s * r, Math.max(1, 0.4 * s * (1 + k * 0.4)));
      }
      c.globalAlpha = 1;
    }
    // Surf where the headlands meet the sea.
    const scroll = t * vFar2;
    for (let x = 0; x <= ww; x += 3) {
      const mk = coastMask(x + scroll);
      if (mk > 0.15) {
        c.fillStyle = `rgba(255,255,255,${0.8 * wCoast * mk})`;
        c.fillRect(x, hy - 1 + Math.sin(t * 4 + x * 0.1), 3, 2 * s);
      }
    }
    // Glint path on the sea.
    const gx = ww * 0.8;
    for (let i = 0; i < 14; i++) {
      const y = hy + 3 + i * (seaB - hy) / 14;
      c.fillStyle = `rgba(255,255,230,${0.4 * wCoast * (0.5 + 0.5 * Math.sin(t * 6 + i * 1.7))})`;
      c.fillRect(gx - (6 + i * 2) * s + Math.sin(i * 3 + t * 2) * 6 * s, y, (12 + i * 4) * s * hash(i), 1.2 * s);
    }
  }

  // Middle layer: houses, temples, towers, hills and cliffs.
  const by = hy + (wh - hy) * 0.16;
  const slot = 64 * s;
  const scrollM = t * vMid;
  const i0 = Math.floor(scrollM / slot) - 4;
  const items: (() => void)[] = [];
  const hillCol = mixc(far2, [40, 110, 50], 0.5);
  for (let i = i0; i < i0 + ww / slot + 6; i++) {
    const X = i * slot;
    const x = X - scrollM;
    const te = (X - ww) / vMid;
    const b = biomeAt(route, clamp(te / T, 0, 1));
    const r = hash(i * 1.37);
    const r2 = hash(i * 2.91 + 5);
    if (b === 'paddy') {
      if (r > 0.62) items.push(() => farmhouse(c, x, by, s, 0));
      else if (r > 0.3) items.push(() => tree(c, x + r2 * slot, by, s, '#3f8a3a', r2));
    } else if (b === 'dusk') {
      if (r > 0.65) items.push(() => farmhouse(c, x, by, s, 0.8));
      else if (r > 0.3) items.push(() => tree(c, x + r2 * slot, by, s, '#3a3550', r2));
    } else if (b === 'town') {
      if (r > 0.86) items.push(() => temple(c, x, by, s));
      else if (r > 0.2) items.push(() => shophouses(c, x, by, s, i));
      else items.push(() => tree(c, x + 20 * s, by, s, '#4f8f3e', r2));
    } else if (b === 'city') {
      if (r > 0.15) items.push(() => cityTower(c, x, by, s, i, haze));
      if (r2 > 0.6) items.push(() => shophouses(c, x + 10 * s, by, s * 0.9, i + 50));
    } else if (b === 'mount') {
      if (r > 0.55) items.unshift(() => hill(c, x, by, s, i, hillCol));
      if (r2 > 0.55) items.push(() => palm(c, x + r * slot, by + 8 * s, s, '#3f8f3a'));
    } else if (b === 'coast') {
      if (((i % 6) + 6) % 6 === 0 && r > 0.3) items.unshift(() => cliff(c, x, hy, by, s, i, t));
      else if (r < 0.1) items.push(() => {
        c.fillStyle = '#5d6a5a';
        c.beginPath();
        c.moveTo(x, by + 6 * s);
        c.quadraticCurveTo(x + 10 * s, by - 18 * s, x + 30 * s, by - 10 * s);
        c.lineTo(x + 40 * s, by + 6 * s);
        c.fill();
        c.fillStyle = 'rgba(255,255,255,0.8)';
        c.fillRect(x - 3 * s, by + 4 * s + Math.sin(t * 5 + i) * s, 46 * s, 2 * s);
      });
    }
  }
  items.forEach((f) => f());
  // A road with scooters through the towns.
  const wTown = w.town ?? 0;
  if (wTown > 0.01) {
    c.globalAlpha = wTown;
    c.fillStyle = '#6d6c68';
    c.fillRect(0, by, ww, 7 * s);
    c.fillStyle = '#f2f0e6';
    const dsp = 26 * s;
    const ds = (t * vMid) % dsp;
    for (let x = -ds; x < ww; x += dsp) c.fillRect(x, by + 3.2 * s, 10 * s, 0.8 * s);
    const vs = vMid * 0.55;
    const sp = 46 * s;
    const sc = t * vs;
    for (let i = Math.floor(sc / sp); i < sc / sp + ww / sp + 2; i++) {
      if (hash(i * 4.4) < 0.45) continue;
      scooter(c, i * sp - sc, by + 6 * s, s * 0.85, i, t);
    }
    c.globalAlpha = 1;
  }
  // Flying egrets (paddies) or birds heading home (dusk).
  const wBirds = (w.paddy ?? 0) + wDusk;
  if (wBirds > 0.05) {
    c.globalAlpha = wBirds;
    for (let i = 0; i < 4; i++) {
      const bx = ((hash(i + 40) * ww * 1.5 - t * (20 + i * 6) * s) % (ww * 1.5) + ww * 1.5) % (ww * 1.5) - ww * 0.25;
      const byy = hy * (0.4 + hash(i + 41) * 0.4) + Math.sin(t * 1.5 + i) * 6 * s;
      if (wDusk > 0.5) {
        c.strokeStyle = '#2a2030';
        c.lineWidth = 1.4 * s;
        const f = Math.sin(t * 8 + i);
        c.beginPath();
        c.moveTo(bx - 6 * s, byy - f * 3 * s);
        c.quadraticCurveTo(bx - 2 * s, byy - 2 * s, bx, byy);
        c.quadraticCurveTo(bx + 2 * s, byy - 2 * s, bx + 6 * s, byy - f * 3 * s);
        c.stroke();
      } else egret(c, bx, byy, s, 1, t);
    }
    c.globalAlpha = 1;
  }

  // Near embankment and blurred bushes.
  const ny = wh * 0.88;
  c.fillStyle = rgb(mixc(ground, [30, 40, 20], 0.35));
  c.fillRect(0, ny, ww, wh - ny);
  const bsp = 34 * s;
  const bsc = t * vNear * 0.7;
  c.fillStyle = rgb(mixc(ground, [20, 30, 10], 0.5), 0.85);
  for (let i = Math.floor(bsc / bsp); i < bsc / bsp + ww / bsp + 2; i++) {
    const r = hash(i * 9.1);
    c.beginPath();
    c.ellipse(i * bsp - bsc, ny + 4 * s, (18 + r * 22) * s, (5 + r * 9) * s, 0, 0, Math.PI * 2);
    c.fill();
  }

  // Poles and catenary wires zipping by.
  const dark = tunnelDark(route, t, T);
  const psp = 280 * s;
  const psc = t * vNear;
  const pcol = wDusk > 0.5 ? '#2a2230' : '#4a4a48';
  const wireY = [wh * 0.1, wh * 0.17];
  for (let i = Math.floor(psc / psp) - 1; i < psc / psp + ww / psp + 2; i++) {
    const x = i * psp - psc;
    // Wires to the next pole.
    c.strokeStyle = 'rgba(30,30,30,0.55)';
    c.lineWidth = 1.1 * s;
    for (const wy of wireY) {
      c.beginPath();
      c.moveTo(x, wy);
      c.quadraticCurveTo(x + psp / 2, wy + 16 * s, x + psp, wy);
      c.stroke();
    }
    // The pole itself, smeared by motion.
    c.fillStyle = pcol;
    c.globalAlpha = 0.16;
    c.fillRect(x - 8 * s, 0, 22 * s, wh);
    c.globalAlpha = 0.9;
    c.fillRect(x - 3 * s, 0, 9 * s, wh);
    c.fillRect(x - 3 * s, wireY[0] - 6 * s, 40 * s, 4 * s);
    c.globalAlpha = 1;
  }

  // Tunnels: darkness with passing lamps.
  if (dark > 0.001) {
    c.fillStyle = `rgba(12,12,16,${dark})`;
    c.fillRect(0, 0, ww, wh);
    c.fillStyle = `rgba(120,110,100,${0.08 * dark})`;
    const rsp = 46 * s;
    const rsc = t * vNear * 0.8;
    for (let x = -(rsc % rsp); x < ww; x += rsp) c.fillRect(x, 0, 6 * s, wh);
    const lsp = 220 * s;
    const lsc = t * vNear;
    for (let i = Math.floor(lsc / lsp); i < lsc / lsp + ww / lsp + 2; i++) {
      const x = i * lsp - lsc;
      const lg = c.createRadialGradient(x, wh * 0.3, 0, x, wh * 0.3, 70 * s);
      lg.addColorStop(0, `rgba(255,190,110,${0.7 * dark})`);
      lg.addColorStop(1, 'rgba(255,170,90,0)');
      c.fillStyle = lg;
      c.fillRect(x - 70 * s, wh * 0.3 - 70 * s, 140 * s, 140 * s);
      c.fillStyle = `rgba(255,220,160,${dark})`;
      c.fillRect(x - 30 * s, wh * 0.3 - 2 * s, 60 * s, 4 * s);
    }
  }
}

/** Glass: interior reflection (stronger in tunnels), highlight streaks and inner shadow. */
function drawGlass(c: CanvasRenderingContext2D, L: TrainLayout, dark: number, dusk: number): void {
  const { ww, wh, s } = L;
  const a = 0.025 + 0.36 * dark + 0.03 * dusk;
  c.save();
  c.globalCompositeOperation = 'screen';
  c.globalAlpha = a;
  // Reflected ceiling lights.
  for (let i = 0; i < 3; i++) {
    const lx = ww * (0.15 + i * 0.3);
    const lg = c.createRadialGradient(lx + 30 * s, wh * 0.08, 0, lx + 30 * s, wh * 0.08, 60 * s);
    lg.addColorStop(0, 'rgba(255,240,200,1)');
    lg.addColorStop(1, 'rgba(255,240,200,0)');
    c.fillStyle = lg;
    c.fillRect(lx - 40 * s, 0, 140 * s, wh * 0.25);
    c.fillStyle = '#fff6dc';
    rrect(c, lx, wh * 0.06, 60 * s, 6 * s, 3 * s);
    c.fill();
  }
  // Reflected seat backs (with white headrest covers) and a passenger.
  for (let i = 0; i < 3; i++) {
    const sx = ww * (0.06 + i * 0.36);
    const sw = ww * 0.26;
    const sy = wh * 0.64;
    c.fillStyle = '#7a5a44';
    rrect(c, sx, sy, sw, wh * 0.45, 22 * s);
    c.fill();
    c.fillStyle = '#d8d0c0';
    rrect(c, sx + sw * 0.18, sy + 6 * s, sw * 0.64, 22 * s, 8 * s);
    c.fill();
  }
  c.fillStyle = '#4a5068';
  const px0 = ww * 0.56;
  c.beginPath();
  c.arc(px0, wh * 0.6, 15 * s, 0, Math.PI * 2);
  c.fill();
  c.beginPath();
  c.moveTo(px0 - 34 * s, wh);
  c.quadraticCurveTo(px0 - 34 * s, wh * 0.66, px0, wh * 0.66);
  c.quadraticCurveTo(px0 + 34 * s, wh * 0.66, px0 + 34 * s, wh);
  c.fill();
  c.restore();
  // Highlight streaks.
  c.save();
  c.globalCompositeOperation = 'screen';
  const g = c.createLinearGradient(0, 0, ww, wh);
  g.addColorStop(0.18, 'rgba(255,255,255,0)');
  g.addColorStop(0.24, 'rgba(255,255,255,0.12)');
  g.addColorStop(0.28, 'rgba(255,255,255,0.03)');
  g.addColorStop(0.32, 'rgba(255,255,255,0.09)');
  g.addColorStop(0.36, 'rgba(255,255,255,0)');
  c.fillStyle = g;
  c.fillRect(0, 0, ww, wh);
  c.restore();
  // Inner shadow from the frame.
  const sh = 18 * s;
  const tg = c.createLinearGradient(0, 0, 0, sh);
  tg.addColorStop(0, 'rgba(0,0,0,0.35)');
  tg.addColorStop(1, 'rgba(0,0,0,0)');
  c.fillStyle = tg;
  c.fillRect(0, 0, ww, sh);
  const lg = c.createLinearGradient(0, 0, sh, 0);
  lg.addColorStop(0, 'rgba(0,0,0,0.25)');
  lg.addColorStop(1, 'rgba(0,0,0,0)');
  c.fillStyle = lg;
  c.fillRect(0, 0, sh, wh);
  const rg = c.createLinearGradient(ww, 0, ww - sh, 0);
  rg.addColorStop(0, 'rgba(0,0,0,0.25)');
  rg.addColorStop(1, 'rgba(0,0,0,0)');
  c.fillStyle = rg;
  c.fillRect(ww - sh, 0, sh, wh);
}

function chime(): void {
  tone(659.3, 1.1, 'sine', 0.09, 0);
  tone(1318.5, 0.6, 'sine', 0.02, 0);
  tone(523.3, 1.5, 'sine', 0.09, 0.5);
  tone(1046.5, 0.8, 'sine', 0.02, 0.5);
}

/** Train-window cutscene, ~9 s. Shows `lines` one after another as captions, then `announce` (an English station announcement); a Skip button ends it early. Calls onDone exactly once. */
export function playTrainRide(route: Route, lines: string[], announce: string, skipLabel: string, onDone: () => void): void {
  const wrap = document.getElementById('wrap');
  const LAYER = 'trainRide';
  const n = lines.length;
  const lineDur = n ? Math.max(2.1, 6.2 / n) : 0;
  const tAnnounce = 0.5 + n * lineDur + 0.2;
  const T = tAnnounce + 2.8;
  let done = false;
  let raf = 0;
  if (!wrap) {
    onDone();
    return;
  }
  layers.open(LAYER);
  const cv = h('canvas', { class: 'trip-cv' });
  const cap = h('div', { class: 'trip-cap', 'aria-live': 'polite' });
  const sign = h('div', { class: 'trip-sign', 'aria-live': 'polite' }, h('span', { class: 'trip-sign-ico' }, '🔔'), h('span', { class: 'trip-sign-txt' }, announce));
  const finish = () => {
    if (done) return;
    done = true;
    root.classList.add('out');
    window.removeEventListener('keydown', onKey, true);
    setTimeout(() => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      root.remove();
      layers.close(LAYER);
      onDone();
    }, 380);
  };
  const skip = h('button', { class: 'btn small trip-skip', onclick: finish }, skipLabel, ' ⏭');
  const root = h('div', { class: 'trip', role: 'dialog', 'aria-modal': 'true' }, cv, sign, cap, skip);
  wrap.append(root);
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      finish();
    }
  };
  window.addEventListener('keydown', onKey, true);

  const ctx = cv.getContext('2d')!;
  let L = trainLayout(1, 1);
  let dpr = 1;
  let back: HTMLCanvasElement | null = null;
  let front: HTMLCanvasElement | null = null;
  const resize = () => {
    const r = root.getBoundingClientRect();
    if (r.width < 2 || r.height < 2) return;
    dpr = sizeCanvas(cv, r.width, r.height);
    L = trainLayout(r.width, r.height);
    const [b, bc] = offscreen(r.width, r.height, dpr);
    drawInteriorBack(bc, L);
    const [f, fc] = offscreen(r.width, r.height, dpr);
    drawInteriorFront(fc, L);
    back = b;
    front = f;
    // Hang the sign in the dark area under the table when there's room, else over the glass.
    const under = L.tableY + L.tableH + 12 * L.s;
    sign.style.top = `${Math.round(L.H - under > 90 ? under + Math.min(30 * L.s, (L.H - under - 60) / 2) : L.wy + 14 * L.s)}px`;
  };
  const ro = new ResizeObserver(resize);
  ro.observe(root);
  resize();

  const t0 = performance.now();
  let line = -1;
  let announced = false;
  const frame = (now: number) => {
    if (done && !root.isConnected) return;
    const t = (now - t0) / 1000;
    // Captions.
    const li = t < 0.5 ? -1 : Math.floor((t - 0.5) / lineDur);
    if (n && li !== line && li < n) {
      line = li;
      if (li >= 0) {
        const el = h('span', { class: 'trip-line' }, lines[li]);
        el.style.animationDuration = `${lineDur}s`;
        cap.replaceChildren(el);
      }
    }
    if (!announced && t >= tAnnounce) {
      announced = true;
      cap.replaceChildren();
      sign.classList.add('show');
      chime();
    }
    if (t >= T) finish();
    if (back && front) {
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.drawImage(back, 0, 0);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.save();
      ctx.translate(L.wx, L.wy);
      rrect(ctx, 0, 0, L.ww, L.wh, 16 * L.s);
      ctx.clip();
      const tt = Math.min(t, T);
      drawScenery(ctx, L, route, tt, T);
      drawGlass(ctx, L, tunnelDark(route, tt, T), route === 'home' ? 1 : 0);
      ctx.restore();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.drawImage(front, 0, 0);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      // Steam from the tea.
      const cx = L.wx + L.ww * 0.78;
      const cy = L.tableY + L.tableH * 0.62 - 38 * L.s;
      ctx.strokeStyle = 'rgba(255,255,255,0.35)';
      ctx.lineWidth = 2 * L.s;
      for (let i = 0; i < 2; i++) {
        const ph = t * 1.4 + i * 1.7;
        ctx.globalAlpha = 0.5 + 0.5 * Math.sin(ph);
        ctx.beginPath();
        ctx.moveTo(cx - 4 * L.s + i * 8 * L.s, cy);
        ctx.bezierCurveTo(cx - 10 * L.s + i * 8 * L.s + Math.sin(ph) * 4, cy - 10 * L.s, cx + 4 * L.s + i * 6 * L.s, cy - 16 * L.s, cx - 2 * L.s + i * 8 * L.s + Math.sin(ph * 1.3) * 5, cy - 28 * L.s);
        ctx.stroke();
      }
      ctx.globalAlpha = 1;
    }
    raf = requestAnimationFrame(frame);
  };
  raf = requestAnimationFrame(frame);
}

// =====================================================================
//  Taipei 101 observatory view
// =====================================================================
type Tod = 'day' | 'sunset' | 'night';
interface TowerPal {
  sky: string[]; mtnFar: string; mtnNear: string; haze: string; ground: string; road: string; river: string; park: string;
  faces: string[]; roof: string; cloud: string;
}
const TPAL: Record<Tod, TowerPal> = {
  day: {
    sky: ['#5d9fd8', '#a9d0ec', '#e2eef0'], mtnFar: '#8eaab4', mtnNear: '#5f8a76', haze: '#dfe9ea', ground: '#86877c', road: '#77787a', river: '#7fa6b8', park: '#6f9a55',
    faces: ['#e2ddd0', '#cfcabe', '#b9bdbd', '#e9e2d2', '#9fb9c8', '#d6cfc2'], roof: '#f0ece2', cloud: 'rgba(255,255,255,0.85)',
  },
  sunset: {
    sky: ['#3b3f7d', '#c96b6b', '#f7b267'], mtnFar: '#8a6684', mtnNear: '#5a4766', haze: '#f2b48a', ground: '#7d6563', road: '#6a5458', river: '#d99a7a', park: '#5d6a4a',
    faces: ['#e7b48f', '#cf9d84', '#b89089', '#f0c39a', '#c49aa0', '#d8a98e'], roof: '#f6caa0', cloud: 'rgba(255,190,160,0.8)',
  },
  night: {
    sky: ['#05081a', '#111a3c', '#2c3363'], mtnFar: '#151b36', mtnNear: '#0c1124', haze: '#3a3560', ground: '#0c0f1f', road: '#2a2430', river: '#0a1430', park: '#0d1a18',
    faces: ['#1a1f36', '#161a2e', '#20253f', '#1c2238', '#1e2440', '#171c31'], roof: '#232945', cloud: 'rgba(120,130,170,0.25)',
  },
};
function timeOfDay(): Tod {
  let t = 12 * 60;
  try {
    t = S().time;
  } catch {
    /* no game yet */
  }
  t = ((t % 1440) + 1440) % 1440;
  if (t >= 1020 && t < 1125) return 'sunset';
  if (t >= 330 && t < 380) return 'sunset';
  if (t >= 380 && t < 1020) return 'day';
  return 'night';
}

interface Car { road: number; u: number; v: number; dir: number; col: number }
interface Road { kind: 'x' | 'z'; at: number; a: number; b: number; w: number }
interface TowerData { W: number; H: number; y0: number; f: number; roads: Road[]; tops: [number, number][]; lights: [number, number, string][] }

const AVENUES = [-7.5, -4.2, -1.9, -0.45, 1.1, 3.2, 6.4];
const STREETS = [2.05, 3.4, 6.6, 9.5, 14, 21];
const riverZ = (x: number) => 4.6 + 0.9 * Math.sin(x * 0.28 + 0.7) + 0.02 * x * x;
const tamsuiX = (z: number) => -6.5 - 0.7 * (z - riverZ(-6.5));

function buildTower(W: number, H: number, dpr: number, tod: Tod): [HTMLCanvasElement, HTMLCanvasElement, TowerData] {
  const P = TPAL[tod];
  const night = tod === 'night';
  const [cv, c] = offscreen(W, H, dpr);
  const y0 = H * 0.34;
  const f = H - y0;
  const kx = Math.max(1, W / H) * 0.9;
  const px = (x: number, z: number) => W / 2 + (x * f * kx) / z;
  const py = (z: number, hgt = 0) => y0 + (f * (1 - hgt)) / z;
  // Sky.
  const sg = c.createLinearGradient(0, 0, 0, y0);
  P.sky.forEach((col, i) => sg.addColorStop(i / (P.sky.length - 1), col));
  c.fillStyle = sg;
  c.fillRect(0, 0, W, y0 + 2);
  if (night) {
    for (let i = 0; i < 90; i++) {
      c.fillStyle = `rgba(255,255,240,${0.3 + hash(i) * 0.6})`;
      const r = hash(i + 7) > 0.92 ? 1.3 : 0.7;
      c.fillRect(hash(i + 1) * W, hash(i + 2) * y0 * 0.85, r, r);
    }
    const mx = W * 0.8;
    const my = y0 * 0.25;
    const mg = c.createRadialGradient(mx, my, 0, mx, my, 40);
    mg.addColorStop(0, 'rgba(255,250,220,0.35)');
    mg.addColorStop(1, 'rgba(255,250,220,0)');
    c.fillStyle = mg;
    c.fillRect(mx - 40, my - 40, 80, 80);
    c.fillStyle = '#fff6d8';
    c.beginPath();
    c.arc(mx, my, 9, 0, Math.PI * 2);
    c.fill();
    c.fillStyle = P.sky[1];
    c.beginPath();
    c.arc(mx + 4, my - 2, 8, 0, Math.PI * 2);
    c.fill();
  } else if (tod === 'sunset') {
    const sx = W * 0.1;
    const sy = y0 - H * 0.07;
    const gg = c.createRadialGradient(sx, sy, 0, sx, sy, W * 0.5);
    gg.addColorStop(0, 'rgba(255,220,150,0.9)');
    gg.addColorStop(1, 'rgba(255,150,100,0)');
    c.fillStyle = gg;
    c.fillRect(0, 0, W, y0);
    c.fillStyle = '#fff0c0';
    c.beginPath();
    c.arc(sx, sy, H * 0.045, 0, Math.PI * 2);
    c.fill();
  } else {
    const sx = W * 0.85;
    const sy = y0 * 0.18;
    const gg = c.createRadialGradient(sx, sy, 0, sx, sy, W * 0.3);
    gg.addColorStop(0, 'rgba(255,255,240,0.8)');
    gg.addColorStop(1, 'rgba(255,255,240,0)');
    c.fillStyle = gg;
    c.fillRect(0, 0, W, y0);
  }
  // Mountains: Guanyinshan far left, Yangmingshan (Qixing, Datun) across the north.
  const gauss = (x: number, c0: number, w0: number) => Math.exp(-(((x - c0) / w0) ** 2));
  const mtn = (col: string, fn: (u: number) => number) => {
    c.fillStyle = col;
    c.beginPath();
    c.moveTo(0, y0 + 2);
    for (let x = 0; x <= W; x += 2) c.lineTo(x, y0 + 1 - fn(x / W) * H);
    c.lineTo(W, y0 + 2);
    c.closePath();
    c.fill();
  };
  mtn(P.mtnFar, (u) => 0.035 + 0.075 * gauss(u, 0.06, 0.05) + 0.04 * gauss(u, 0.13, 0.04) + 0.05 * gauss(u, 0.75, 0.2) + 0.012 * Math.sin(u * 40));
  mtn(P.mtnNear, (u) => 0.02 + 0.13 * gauss(u, 0.42, 0.1) + 0.1 * gauss(u, 0.27, 0.08) + 0.06 * gauss(u, 0.58, 0.12) + 0.03 * gauss(u, 0.9, 0.1) + 0.006 * Math.sin(u * 90) + 0.004 * Math.sin(u * 230));
  if (night) {
    // Lights along the mountain roads.
    for (let i = 0; i < 40; i++) {
      const u = 0.18 + hash(i * 3.3) * 0.5;
      const hgt = (0.02 + 0.13 * gauss(u, 0.42, 0.1) + 0.1 * gauss(u, 0.27, 0.08)) * H * hash(i * 5.1);
      c.fillStyle = 'rgba(255,214,140,0.7)';
      c.fillRect(u * W, y0 - hgt, 1, 1);
    }
  }
  const mh = c.createLinearGradient(0, y0 - H * 0.08, 0, y0);
  mh.addColorStop(0, `${P.haze}00`);
  mh.addColorStop(1, `${P.haze}cc`);
  c.fillStyle = mh;
  c.fillRect(0, y0 - H * 0.08, W, H * 0.08 + 1);
  // Ground plane.
  const gg = c.createLinearGradient(0, y0, 0, H);
  gg.addColorStop(0, P.haze);
  gg.addColorStop(0.15, P.ground);
  gg.addColorStop(1, P.ground);
  c.fillStyle = gg;
  c.fillRect(0, y0, W, H - y0);
  const quad = (pts: [number, number][], col: string) => {
    c.fillStyle = col;
    c.beginPath();
    pts.forEach(([x, z], i) => (i ? c.lineTo(px(x, z), py(z)) : c.moveTo(px(x, z), py(z))));
    c.closePath();
    c.fill();
  };
  // Riverside parks then rivers.
  const ribbon = (pts: [number, number][], half: number, col: string, alongX: boolean) => {
    const a: [number, number][] = [];
    const b: [number, number][] = [];
    for (const [x, z] of pts) {
      if (alongX) {
        a.push([x, z - half]);
        b.push([x, z + half]);
      } else {
        a.push([x - half, z]);
        b.push([x + half, z]);
      }
    }
    quad([...a, ...b.reverse()], col);
  };
  const kPts: [number, number][] = [];
  for (let x = -40; x <= 40; x += 0.5) kPts.push([x, riverZ(x)]);
  const tPts: [number, number][] = [];
  for (let z = riverZ(-6.5); z <= 70; z += 0.5) tPts.push([tamsuiX(z), z]);
  ribbon(kPts, 0.7, P.park, true);
  ribbon(tPts, 1.3, P.park, false);
  ribbon(kPts, 0.32, P.river, true);
  ribbon(tPts, 0.75, P.river, false);
  if (!night) {
    ribbon(kPts.map(([x, z]) => [x, z - 0.1] as [number, number]), 0.04, 'rgba(255,255,255,0.25)', true);
  } else {
    // Reflections of city lights on the water.
    for (let i = 0; i < 70; i++) {
      const x = -14 + hash(i * 2.2) * 28;
      const z = riverZ(x) + (hash(i * 3.1) - 0.5) * 0.4;
      c.fillStyle = i % 3 ? 'rgba(255,200,120,0.45)' : 'rgba(180,220,255,0.4)';
      c.fillRect(px(x, z), py(z), 2, 0.8);
    }
  }
  // Roads (avenues run north into the distance, streets cross them).
  const roads: Road[] = [];
  for (const x of AVENUES) roads.push({ kind: 'z', at: x, a: 1, b: 60, w: x === -0.45 ? 0.16 : 0.1 });
  for (const z of STREETS) roads.push({ kind: 'x', at: z, a: -40, b: 40, w: 0.07 + z * 0.004 });
  for (const r of roads) {
    if (r.kind === 'z') quad([[r.at - r.w, r.a], [r.at + r.w, r.a], [r.at + r.w * 1.2, r.b], [r.at - r.w * 1.2, r.b]], P.road);
    else quad([[r.a, r.at - r.w], [r.b, r.at - r.w], [r.b, r.at + r.w], [r.a, r.at + r.w]], P.road);
  }
  if (night) {
    // Orange sodium glow over the roads, and the glitter of the whole basin.
    for (const r of roads) {
      const col = 'rgba(255,150,60,0.15)';
      if (r.kind === 'z') quad([[r.at - r.w * 1.6, r.a], [r.at + r.w * 1.6, r.a], [r.at + r.w * 1.8, r.b], [r.at - r.w * 1.8, r.b]], col);
      else quad([[r.a, r.at - r.w * 1.6], [r.b, r.at - r.w * 1.6], [r.b, r.at + r.w * 1.6], [r.a, r.at + r.w * 1.6]], col);
    }
    for (let i = 0; i < 2600; i++) {
      const z2 = 4 + hash(i * 1.31) ** 0.7 * 50;
      const x2 = (hash(i * 2.71) - 0.5) * 2 * ((W / 2 / (f * kx)) * z2 + 1);
      if (onRiverG(x2, z2)) continue;
      const r = hash(i * 3.97);
      c.fillStyle = r < 0.7 ? `rgba(255,200,120,${0.35 + r * 0.6})` : `rgba(210,230,255,${0.3 + (r - 0.7) * 1.5})`;
      c.fillRect(px(x2, z2), py(z2) - 0.5, r > 0.95 ? 1.4 : 0.9, 0.9);
    }
    c.fillStyle = 'rgba(255,190,110,0.7)';
    for (const r of roads) {
      for (let k = 0; k < 60; k++) {
        const u = k / 60;
        const z = r.kind === 'z' ? lerp(1.05, 30, u * u) : r.at;
        const x = r.kind === 'z' ? r.at + r.w : lerp(-20, 20, u);
        const X = px(x, z);
        if (X < -2 || X > W + 2) continue;
        c.fillRect(X, py(z) - 0.5, 1, 1);
      }
    }
  }
  // Buildings.
  function onRiverG(x: number, z: number): boolean {
    return Math.abs(z - riverZ(x)) < 0.36 || (z > riverZ(-6.5) && Math.abs(x - tamsuiX(z)) < 0.8);
  }
  const onRoad = (x: number, z: number, half: number) => {
    for (const a of AVENUES) if (Math.abs(x - a) < half + (a === -0.45 ? 0.17 : 0.11)) return true;
    for (const s of STREETS) if (Math.abs(z - s) < half * 0.8 + 0.06) return true;
    return false;
  };
  const onRiver = (x: number, z: number, half: number) =>
    Math.abs(z - riverZ(x)) < 0.7 + half || (z > riverZ(-6.5) - 1 && Math.abs(x - tamsuiX(z)) < 1.3 + half);
  interface B { x: number; z: number; w: number; d: number; hgt: number; col: number; seed: number }
  const bs: B[] = [];
  let z = 1.05;
  let row = 0;
  while (z < 30) {
    const cell = 0.22 * (1 + z * 0.09);
    const xr = (W / 2 / (f * kx)) * z + 1;
    for (let x = -xr + (row % 2) * cell * 0.5; x < xr; x += cell) {
      const seed = row * 1000 + Math.round(x * 10);
      const r = hash(seed);
      const half = cell * 0.4;
      if (onRoad(x, z, half) || onRiver(x, z, half)) continue;
      if (r < 0.06) {
        quad([[x - half, z - half], [x + half, z - half], [x + half, z + half], [x - half, z + half]], P.park);
        continue;
      }
      // Xinyi skyscrapers cluster near the tower; the rest is mid-rise.
      const xinyi = z < 4 ? gauss(x, 0.3, 1.6) : 0;
      // Heights as a fraction of our own 382 m eye height (/4, see `top`).
      let hgt = 0.006 + hash(seed + 1) ** 2 * 0.03;
      if (hash(seed + 2) < 0.12 + xinyi * 0.4) hgt += 0.015 + hash(seed + 3) ** 1.5 * (0.04 + xinyi * 0.08);
      bs.push({ x: x + (hash(seed + 5) - 0.5) * cell * 0.2, z, w: cell * (0.68 + hash(seed + 6) * 0.27), d: cell * (0.62 + hash(seed + 7) * 0.3), hgt, col: Math.floor(hash(seed + 8) * P.faces.length), seed });
    }
    z += cell;
    row++;
  }
  bs.sort((a, b) => b.z - a.z || Math.abs(b.x) - Math.abs(a.x));
  const tops: [number, number][] = [];
  const lights: [number, number, string][] = [];
  const hz = hex(P.haze);
  for (const b of bs) {
    const fade = clamp((b.z - 2) / 26, 0, 1) ** 0.8 * 0.85;
    const face = mixc(hex(P.faces[b.col]), hz, fade);
    const xl = b.x - b.w / 2;
    const xr2 = b.x + b.w / 2;
    const zf = b.z - b.d / 2;
    const zb = b.z + b.d / 2;
    const top = (z2: number) => y0 + (f / z2) * (1 - b.hgt * 4);
    // Height in "ground units": screen height = hgt*4*f/z.
    const fx0 = px(xl, zf);
    const fx1 = px(xr2, zf);
    const fyB = py(zf);
    const fyT = top(zf);
    if (fx1 < -4 || fx0 > W + 4) continue;
    const bx0 = px(xl, zb);
    const bx1 = px(xr2, zb);
    const byT = top(zb);
    // Side face towards the centre line.
    c.fillStyle = rgb(mixc(face, [0, 0, 0], night ? 0.2 : tod === 'sunset' ? 0.3 : 0.22));
    c.beginPath();
    if (b.x > 0) {
      c.moveTo(fx0, fyB);
      c.lineTo(fx0, fyT);
      c.lineTo(bx0, byT);
      c.lineTo(bx0, py(zb));
    } else {
      c.moveTo(fx1, fyB);
      c.lineTo(fx1, fyT);
      c.lineTo(bx1, byT);
      c.lineTo(bx1, py(zb));
    }
    c.closePath();
    c.fill();
    // Front face.
    c.fillStyle = rgb(face);
    c.fillRect(fx0, fyT, fx1 - fx0, fyB - fyT);
    // Roof.
    const rv = hash(b.seed + 11);
    const roofC = mixc(hex(P.roof), [0, 0, 0], night ? 0 : rv < 0.3 ? 0.18 : rv < 0.45 ? 0.3 : rv * 0.08);
    c.fillStyle = rgb(mixc(rv > 0.9 && !night ? hex('#c9a27a') : roofC, hz, fade));
    c.beginPath();
    c.moveTo(fx0, fyT);
    c.lineTo(fx1, fyT);
    c.lineTo(bx1, byT);
    c.lineTo(bx0, byT);
    c.closePath();
    c.fill();
    const fw = fx1 - fx0;
    const fh = fyB - fyT;
    // Rooftop clutter: water tanks and stair huts.
    if (fw > 10 && byT - fyT < -3) {
      c.fillStyle = rgb(mixc(night ? hex('#2a3050') : hex('#8e8c86'), hz, fade));
      const rx = lerp(fx0, fx1, 0.2 + hash(b.seed + 12) * 0.4);
      const ry = lerp(fyT, byT, 0.5);
      c.fillRect(rx, ry - fw * 0.08, fw * 0.18, fw * 0.12);
      if (hash(b.seed + 13) > 0.5) {
        c.fillStyle = rgb(mixc(night ? hex('#323a5c') : hex('#5f8fa8'), hz, fade));
        c.fillRect(rx + fw * 0.3, ry - fw * 0.06, fw * 0.1, fw * 0.1);
      }
    }
    // Windows: lit dots at night, glass bands by day.
    if (night) {
      const nx = Math.max(1, Math.floor(fw / 3));
      const ny = Math.max(1, Math.floor(fh / 3.2));
      for (let i = 0; i < nx; i++)
        for (let j = 0; j < ny; j++) {
          const r = hash(b.seed * 0.37 + i * 7.1 + j * 13.3);
          if (r > 0.5) continue;
          const col = r < 0.12 ? 'rgba(200,225,255,0.85)' : 'rgba(255,205,120,0.9)';
          c.fillStyle = col;
          const wx = fx0 + (i + 0.5) * (fw / nx) - 0.6;
          const wy = fyT + (j + 0.5) * (fh / ny) - 0.5;
          c.fillRect(wx, wy, fw > 6 ? 1.3 : 0.9, 1);
          if (r < 0.015 && lights.length < 80) lights.push([wx, wy, col]);
        }
    } else if (fw > 3 && fh > 4) {
      c.fillStyle = tod === 'sunset' ? 'rgba(255,230,180,0.25)' : 'rgba(70,90,110,0.22)';
      for (let y = fyT + 1.5; y < fyB - 1; y += 2.6) c.fillRect(fx0 + 0.6, y, fw - 1.2, 0.9);
    }
    if (tod === 'sunset' && b.x > -1) {
      c.fillStyle = 'rgba(255,170,90,0.18)';
      c.fillRect(fx0, fyT, fw * 0.5, fh);
    }
    if (b.hgt > 0.06 && b.z < 10) tops.push([(fx0 + fx1) / 2, fyT]);
  }
  // Haze band at the horizon.
  const hb = c.createLinearGradient(0, y0 - H * 0.05, 0, y0 + H * 0.18);
  hb.addColorStop(0, `${P.haze}00`);
  hb.addColorStop(0.22, `${P.haze}${night ? '99' : 'cc'}`);
  hb.addColorStop(0.45, `${P.haze}55`);
  hb.addColorStop(1, `${P.haze}00`);
  c.fillStyle = hb;
  c.fillRect(0, y0 - H * 0.05, W, H * 0.23);

  // Front layer: observatory window mullions, rail and glass reflections.
  const [fr, fc] = offscreen(W, H, dpr);
  const mcol = night ? '#05060c' : '#1d1a18';
  fc.fillStyle = mcol;
  for (const [x0, lean] of [[0, 1], [W, -1]] as [number, number][]) {
    fc.beginPath();
    fc.moveTo(x0, 0);
    fc.lineTo(x0 + lean * 16, 0);
    fc.lineTo(x0 + lean * 7, H);
    fc.lineTo(x0, H);
    fc.closePath();
    fc.fill();
  }
  fc.fillRect(W * 0.66 - 1.5, 0, 3, H);
  fc.fillRect(0, 0, W, 5);
  const rail = fc.createLinearGradient(0, H - 14, 0, H);
  rail.addColorStop(0, night ? '#2a2c38' : '#9a9a96');
  rail.addColorStop(0.35, night ? '#111' : '#4a4844');
  rail.addColorStop(1, mcol);
  fc.fillStyle = rail;
  fc.fillRect(0, H - 12, W, 12);
  fc.globalCompositeOperation = 'screen';
  const gl = fc.createLinearGradient(0, 0, W, H);
  gl.addColorStop(0.08, 'rgba(255,255,255,0)');
  gl.addColorStop(0.14, `rgba(255,255,255,${night ? 0.05 : 0.1})`);
  gl.addColorStop(0.2, 'rgba(255,255,255,0)');
  gl.addColorStop(0.7, 'rgba(255,255,255,0)');
  gl.addColorStop(0.76, `rgba(255,255,255,${night ? 0.04 : 0.08})`);
  gl.addColorStop(0.8, 'rgba(255,255,255,0)');
  fc.fillStyle = gl;
  fc.fillRect(0, 0, W, H);
  if (night) {
    // Faint reflection of the observatory's own lights.
    fc.fillStyle = 'rgba(255,220,170,0.08)';
    for (let i = 0; i < 4; i++) fc.fillRect(W * (0.1 + i * 0.22), H * 0.05, W * 0.08, 3);
  }
  return [cv, fr, { W, H, y0, f, roads, tops: tops.slice(-6), lights }];
}

/** The view from the Taipei 101 observatory: an animated panorama canvas plus whatever DOM content I pass under it (text, quiz buttons). Returns a close function. */
export function openTowerView(title: string, below: HTMLElement[], onClose?: () => void): () => void {
  const LAYER = 'towerView';
  const wrap = document.getElementById('wrap');
  let closed = false;
  let raf = 0;
  const cv = h('canvas', { class: 'tower-cv' });
  const view = h('div', { class: 'tower-view' }, cv, h('span', { class: 'tower-badge' }, '89F · 382 m'));
  const close = () => {
    if (closed) return;
    closed = true;
    cancelAnimationFrame(raf);
    ro.disconnect();
    window.removeEventListener('keydown', onKey, true);
    root.remove();
    layers.close(LAYER);
    onClose?.();
  };
  const root = h(
    'div',
    { class: 'tower', role: 'dialog', 'aria-modal': 'true', 'aria-label': title },
    h(
      'div',
      { class: 'tower-box' },
      h('div', { class: 'tower-head' }, h('b', null, title)),
      view,
      below.length ? h('div', { class: 'tower-card' }, below) : null,
      h('button', { class: 'btn gold tower-close', onclick: close }, m().common.close),
    ),
  );
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      close();
    }
  };
  const ro = new ResizeObserver(() => resize());
  if (!wrap) {
    closed = true;
    onClose?.();
    return () => {};
  }
  layers.open(LAYER);
  wrap.append(root);
  window.addEventListener('keydown', onKey, true);

  const ctx = cv.getContext('2d')!;
  const tod = timeOfDay();
  const P = TPAL[tod];
  let dpr = 1;
  let stat: HTMLCanvasElement | null = null;
  let front: HTMLCanvasElement | null = null;
  let D: TowerData | null = null;
  let cars: Car[] = [];
  const resize = () => {
    const r = view.getBoundingClientRect();
    if (r.width < 2 || r.height < 2) return;
    if (D && Math.abs(D.W - r.width) < 1 && Math.abs(D.H - r.height) < 1) return;
    dpr = sizeCanvas(cv, r.width, r.height);
    [stat, front, D] = buildTower(r.width, r.height, dpr, tod);
    cars = [];
    for (let i = 0; i < (tod === 'night' ? 240 : 160); i++) {
      const road = Math.floor(hash(i * 1.9) * D.roads.length);
      cars.push({ road, u: hash(i * 2.3), v: 0.012 + hash(i * 3.7) * 0.02, dir: hash(i * 4.1) > 0.5 ? 1 : -1, col: hash(i * 5.3) });
    }
  };
  ro.observe(view);
  resize();

  const night = tod === 'night';
  let last = performance.now();
  const t0 = last;
  const frame = (now: number) => {
    if (closed) return;
    const dt = Math.min(0.05, (now - last) / 1000);
    last = now;
    const t = (now - t0) / 1000;
    if (stat && front && D) {
      const { W, H, y0, f } = D;
      const kx = Math.max(1, W / H) * 0.9;
      const px = (x: number, z: number) => W / 2 + (x * f * kx) / z;
      const py = (z: number) => y0 + f / z;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.drawImage(stat, 0, 0);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      // Cars as light dots on the avenues.
      for (const car of cars) {
        car.u = (car.u + car.v * dt * car.dir + 1) % 1;
        const r = D.roads[car.road];
        let x: number, z: number;
        if (r.kind === 'z') {
          z = lerp(1.02, 24, car.u * car.u);
          x = r.at + car.dir * r.w * 0.45;
        } else {
          z = r.at + car.dir * r.w * 0.4;
          x = lerp(-18, 18, car.u);
        }
        const sx = px(x, z);
        if (sx < 0 || sx > W) continue;
        const sy = py(z);
        const sz = clamp(2.6 / z, 0.6, 2.2);
        if (night) {
          const toward = r.kind === 'z' ? car.dir < 0 : car.col > 0.5;
          ctx.fillStyle = toward ? 'rgba(255,250,225,0.95)' : 'rgba(255,70,60,0.9)';
          ctx.fillRect(sx - sz / 2, sy - sz / 2, sz, sz);
          if (sz > 1.2) {
            ctx.fillStyle = toward ? 'rgba(255,240,200,0.18)' : 'rgba(255,60,50,0.15)';
            ctx.fillRect(sx - sz * 1.5, sy - sz * 1.5, sz * 3, sz * 3);
          }
        } else {
          ctx.fillStyle = car.col < 0.28 ? '#f5c518' : car.col < 0.6 ? '#f4f4f0' : car.col < 0.8 ? '#44474d' : '#c0392b';
          ctx.fillRect(sx - sz / 2, sy - sz / 3, sz, sz * 0.66);
        }
      }
      // Window twinkles and aviation lights at night; sparkles on the river by day.
      if (night) {
        for (let i = 0; i < D.lights.length; i++) {
          const [lx, ly, col] = D.lights[i];
          if (Math.sin(t * (0.6 + hash(i) * 1.4) + i * 2) > 0.7) continue;
          ctx.fillStyle = col;
          ctx.fillRect(lx - 0.3, ly - 0.3, 1.8, 1.6);
        }
      } else {
        for (let i = 0; i < 18; i++) {
          const x = -10 + hash(i * 6.1) * 20;
          const z = riverZ(x);
          const a = Math.max(0, Math.sin(t * 2.4 + i * 1.9));
          ctx.fillStyle = `rgba(255,255,240,${a * 0.8})`;
          ctx.fillRect(px(x, z), py(z) - 0.5, 1.6, 1);
        }
      }
      const blink = Math.sin(t * 3.2) > 0.3;
      if (blink)
        for (const [tx, ty] of D.tops) {
          ctx.fillStyle = 'rgba(255,40,30,0.95)';
          ctx.fillRect(tx - 1, ty - 2.5, 2, 2);
          ctx.fillStyle = 'rgba(255,40,30,0.25)';
          ctx.beginPath();
          ctx.arc(tx, ty - 1.5, 4, 0, Math.PI * 2);
          ctx.fill();
        }
      // Drifting clouds.
      ctx.fillStyle = P.cloud;
      for (let i = 0; i < 6; i++) {
        const span = W + 200;
        const cx = ((hash(i + 3) * span + t * (4 + i * 1.5)) % span) - 100;
        const low = i < 2;
        const cy = low ? y0 - H * 0.02 - i * H * 0.03 : H * (0.05 + hash(i + 8) * 0.18);
        const r = (low ? 10 : 8 + hash(i) * 12) * (H / 300);
        ctx.globalAlpha = low ? 0.55 : 0.9;
        ctx.beginPath();
        ctx.ellipse(cx, cy, r * 3, r * 0.6, 0, 0, Math.PI * 2);
        ctx.ellipse(cx - r * 1.2, cy - r * 0.15, r * 1.4, r * 0.6, 0, 0, Math.PI * 2);
        ctx.ellipse(cx + r * 0.8, cy - r * 0.4, r * 1.5, r * 0.8, 0, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
      // A plane gliding down towards Songshan airport.
      {
        const per = 26;
        const u = (t % per) / per;
        const x = lerp(W * 1.1, W * 0.25, u);
        const y = lerp(H * 0.08, y0 - H * 0.05, u);
        ctx.fillStyle = night ? '#1a1f33' : '#f2f2f2';
        ctx.save();
        ctx.translate(x, y);
        ctx.rotate(-0.12);
        ctx.fillRect(-7, -1, 14, 2.2);
        ctx.beginPath();
        ctx.moveTo(-1, 0);
        ctx.lineTo(3, 0);
        ctx.lineTo(-3, 5);
        ctx.closePath();
        ctx.fill();
        ctx.fillRect(5, -4, 2, 4);
        ctx.restore();
        if (night || Math.sin(t * 6) > 0) {
          ctx.fillStyle = Math.sin(t * 6) > 0 ? '#ff3b30' : 'rgba(255,255,255,0.9)';
          ctx.fillRect(x - 1, y - 1, 2, 2);
        }
      }
      // A few birds.
      if (!night) {
        ctx.strokeStyle = tod === 'sunset' ? '#3a2a3a' : '#2c3036';
        ctx.lineWidth = 1.2;
        for (let i = 0; i < 4; i++) {
          const span = W + 80;
          const bx = ((hash(i + 20) * span - t * (9 + i * 2)) % span + span) % span - 40;
          const by = H * (0.12 + hash(i + 21) * 0.2) + Math.sin(t + i) * 4;
          const fl = Math.sin(t * 9 + i * 2) * 2.5;
          ctx.beginPath();
          ctx.moveTo(bx - 4, by - fl);
          ctx.quadraticCurveTo(bx - 1.5, by - 1, bx, by);
          ctx.quadraticCurveTo(bx + 1.5, by - 1, bx + 4, by - fl);
          ctx.stroke();
        }
      }
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.drawImage(front, 0, 0);
    }
    raf = requestAnimationFrame(frame);
  };
  raf = requestAnimationFrame(frame);
  return close;
}
